import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  IconButton, 
  Avatar, 
  CircularProgress 
} from '@material-ui/core';
import { 
  Chat as ChatIcon, 
  Close as CloseIcon, 
  Send as SendIcon, 
  Remove as MinimizeIcon,
  CropSquare as MaximizeIcon,
  FilterNone as RestoreIcon
} from '@material-ui/icons';
import './Chatbot.scss';
import chatbotAPI from './ChatbotAPI';

const SimpleChatbot = () => {
  // Core states
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // UI states
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Drag state - shared between bubble and chat window
  const [position, setPosition] = useState({ 
    x: window.innerWidth - 80, 
    y: window.innerHeight - 80 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  
  // Refs
  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const elementStartPos = useRef({ x: 0, y: 0 });

  // Handle mouse down for dragging (works for both bubble and chat)
  const handleMouseDown = (e) => {
    if (isMaximized) return;
    
    // Prevent default to avoid text selection
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    setHasDragged(false);
    
    // Store where the mouse clicked
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
    
    // Store current element position
    elementStartPos.current = {
      x: position.x,
      y: position.y
    };
    
    // Add grabbing cursor immediately
    document.body.style.cursor = 'grabbing';
  };

  // Handle mouse move and mouse up
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        // Calculate how far the mouse moved
        const deltaX = e.clientX - dragStartPos.current.x;
        const deltaY = e.clientY - dragStartPos.current.y;
        
        // Mark as dragged if moved more than 3 pixels (reduced threshold for better responsiveness)
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          setHasDragged(true);
        }
        
        // Calculate new position (add delta to initial position)
        let newX = elementStartPos.current.x + deltaX;
        let newY = elementStartPos.current.y + deltaY;
        
        // Constrain to viewport with some padding
        const elementWidth = isOpen ? 320 : 56;
        const elementHeight = isOpen ? (isMinimized ? 60 : 450) : 56;
        const padding = 10;
        
        newX = Math.max(padding, Math.min(newX, window.innerWidth - elementWidth - padding));
        newY = Math.max(padding, Math.min(newY, window.innerHeight - elementHeight - padding));
        
        setPosition({ x: newX, y: newY });
    });
  };
  
    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      // Reset hasDragged after a short delay to prevent click
      setTimeout(() => setHasDragged(false), 50);
    };
    
    if (isDragging) {
      // Use capture phase for better responsiveness
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, isOpen, isMinimized]);

  // Toggle functions
  const toggleChat = () => {
    // Don't open if we just finished dragging
    if (hasDragged) return;
    
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsMinimized(false);
      setIsMaximized(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const toggleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  const toggleMaximize = (e) => {
    e.stopPropagation();
    setIsMaximized(!isMaximized);
  };

  // Send message
  const sendMessage = async () => {
    if (!inputValue.trim()) return;
    
    const userMessage = {
          id: Date.now(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
          setIsTyping(true);
          
    try {
      const response = await chatbotAPI.sendMessage(inputValue);
      const botMessage = {
              id: Date.now() + 1,
        text: response,
              sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "Sorry, I couldn't connect to the server. Please try again.",
                sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto scroll to bottom
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        text: "Hello! I'm D.I.V.A., your Adopt-a-Highway assistant. How can I help you today?",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  // Format time
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Render draggable chat bubble when closed
  if (!isOpen) {
    return (
      <IconButton
        onMouseDown={handleMouseDown}
        onClick={toggleChat}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: 56,
          height: 56,
          backgroundColor: '#1976d2',
          borderRadius: '50%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          cursor: isDragging ? 'grabbing' : 'grab',
          zIndex: 9999,
          padding: 0,
          willChange: isDragging ? 'transform' : 'auto',
          transition: 'none',
          transform: 'translateZ(0)' // Enable hardware acceleration
        }}
      >
        <ChatIcon style={{ color: 'white', fontSize: 24, pointerEvents: 'none' }} />
      </IconButton>
    );
  }

  // Calculate chat window position
  const getChatPosition = () => {
    if (isMaximized) {
      // Right half of screen
      return {
        right: 0,
        top: 0,
        left: 'auto',
        width: '50vw',
        height: '100vh'
      };
    }
    
    // For normal position, use CSS calc to prevent overflow
    const chatWidth = 320;
    const chatHeight = isMinimized ? 60 : 450;
    
    return {
      left: `min(${position.x}px, calc(100vw - ${chatWidth + 20}px))`,
      top: `min(${position.y}px, calc(100vh - ${chatHeight + 20}px))`,
      width: chatWidth,
      height: chatHeight
    };
  };

  // Render chat window when open
    return (
      <Box 
      className="chatbot-window"
        style={{ 
        position: 'fixed',
        ...getChatPosition(),
        backgroundColor: 'white',
        borderRadius: isMaximized ? 0 : 12,
        boxShadow: isDragging ? '0 8px 32px rgba(0,0,0,0.2)' : '0 4px 24px rgba(0,0,0,0.15)',
          display: 'flex', 
          flexDirection: 'column', 
        overflow: 'hidden',
        zIndex: 9999,
        transition: isMaximized && !isDragging ? 'all 0.3s ease' : 'none',
        willChange: isDragging ? 'transform' : 'auto',
        transform: isDragging ? 'translateZ(0) scale(1.02)' : 'translateZ(0)'
      }}
    >
      {/* Header */}
      <Box
        onMouseDown={handleMouseDown}
            style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#1976d2',
          color: 'white',
          cursor: isMaximized ? 'default' : isDragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
      >
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar style={{ width: 28, height: 28, backgroundColor: '#1565c0' }}>
            AI
          </Avatar>
          <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
            D.I.V.A.
              </Typography>
        </Box>
        
        <Box style={{ display: 'flex', gap: 4 }}>
          <IconButton
                    size="small"
            onClick={toggleMaximize}
            style={{ color: 'white', padding: 4 }}
          >
            {isMaximized ? <RestoreIcon fontSize="small" /> : <MaximizeIcon fontSize="small" />}
          </IconButton>
                  <IconButton 
                    size="small" 
            onClick={toggleMinimize}
            style={{ color: 'white', padding: 4 }}
          >
            <MinimizeIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
            onClick={toggleChat}
            style={{ color: 'white', padding: 4 }}
          >
            <CloseIcon fontSize="small" />
                  </IconButton>
              </Box>
          </Box>
          
      {/* Chat Body - Hidden when minimized */}
      {!isMinimized && (
        <>
          <Box
            ref={chatBodyRef}
              style={{ 
              flex: 1,
              overflowY: 'auto',
              padding: 16,
              backgroundColor: '#f5f5f5'
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
          style={{ 
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 12
                }}
              >
                <Box
            style={{ 
                    maxWidth: '70%',
                    padding: '8px 12px',
                    borderRadius: 12,
                    backgroundColor: message.sender === 'user' ? '#1976d2' : 'white',
                    color: message.sender === 'user' ? 'white' : '#333'
                  }}
                >
                  <Typography variant="body2">{message.text}</Typography>
                  <Typography 
                    variant="caption" 
                    style={{ 
                      opacity: 0.7, 
                      fontSize: 10,
                      display: 'block',
                      marginTop: 4
                    }}
                  >
                    {formatTime(message.timestamp)}
                  </Typography>
              </Box>
            </Box>
            ))}
                
                {isTyping && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="textSecondary">
                  D.I.V.A. is typing...
                      </Typography>
                      </Box>
            )}
                    </Box>

          {/* Input Area */}
          <Box
            style={{
              padding: 12,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}
          >
            <Box style={{ display: 'flex', gap: 8 }}>
                  <TextField
                    fullWidth
                size="small"
                placeholder="Type your message..."
                    value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    inputRef={inputRef}
                variant="outlined"
                    InputProps={{
                  style: { borderRadius: 20 }
                    }}
                  />
                      <IconButton 
                onClick={sendMessage}
                disabled={!inputValue.trim()}
                        style={{
                  backgroundColor: '#1976d2',
                          color: 'white',
                  width: 36,
                  height: 36
                }}
              >
                <SendIcon fontSize="small" />
                      </IconButton>
                </Box>
              </Box>
        </>
      )}
    </Box>
  );
};

export default SimpleChatbot;